import { Icon } from "@iconify/react"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { ActionIcon, Textarea, UnstyledButton } from "@mantine/core"
import classes from "../styles.module.css"
import type { IViewProps } from "./interfaces"

/**
 * Dumb view for EditableAnalysisField. The smart wrapper at ../index.tsx
 * owns the patchAnalysis API call, draft + saving state, and the
 * FieldEditHistory modal; this component renders the inline-editable text
 * given a value, an editing flag, and the relevant callbacks.
 */
export function EditableAnalysisFieldView({
  value,
  canEdit,
  isEdited,
  editing,
  draft,
  saving,
  onStartEdit,
  onDraftChange,
  onCommit,
  onCancel,
  onRequestHistory,
}: IViewProps): React.JSX.Element {
  const { i18n } = useLingui()

  if (editing) {
    return (
      <Textarea
        value={draft}
        onChange={(e) => onDraftChange(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel()
        }}
        onBlur={onCommit}
        disabled={saving}
        autosize
        minRows={1}
        maxRows={12}
        autoFocus
        size="sm"
        classNames={{ input: classes.input }}
      />
    )
  }

  const textContent = canEdit ? (
    // component="span": a <button> is an atomic inline box, so multi-line
    // values couldn't share line boxes with a trailing seek timestamp — the
    // chip wrapped onto its own line. A span participates in normal text
    // flow; role/tabIndex/onKeyDown keep it keyboard-operable.
    <UnstyledButton
      component="span"
      role="button"
      tabIndex={0}
      className={`${classes.editableText} ${classes.inlineEditableText}`}
      onClick={onStartEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onStartEdit()
        }
      }}
      title={i18n._(msg`Click to edit`)}
    >
      {value}
    </UnstyledButton>
  ) : (
    <span>{value}</span>
  )

  return (
    <span className={classes.wrapper}>
      {textContent}
      {isEdited && (
        <ActionIcon
          component="span"
          variant="transparent"
          size="xs"
          className={classes.editedMark}
          onClick={(e) => {
            e.stopPropagation()
            onRequestHistory()
          }}
          title={i18n._(msg`View edit history`)}
        >
          <Icon icon="tabler:edit" width={12} height={12} />
        </ActionIcon>
      )}
    </span>
  )
}
